/* eslint-disable no-unused-vars */
import React, { useState, useEffect, useRef } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  FlatList,
  StatusBar,
  TextInput,
  Image,
  Alert,
  RefreshControl,
  ActivityIndicator,
  Modal,
  Dimensions,
  Linking,
  Share,
  ScrollView,
} from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import {
  MaterialCommunityIcons,
  Ionicons,
} from '@expo/vector-icons';
import { useNavigation, useFocusEffect } from '@react-navigation/native';
import { LinearGradient } from 'expo-linear-gradient';
import { SafeAreaView } from 'react-native-safe-area-context';

const { width } = Dimensions.get('window');

const StudyGroups = () => {
  const navigation = useNavigation();
  const [darkMode, setDarkMode] = useState(false);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState('all');
  const [myGroups, setMyGroups] = useState([]);
  const [allStudyGroups, setAllStudyGroups] = useState([]);
  const [filteredGroups, setFilteredGroups] = useState([]);
  const [createModalVisible, setCreateModalVisible] = useState(false);
  const [detailsModalVisible, setDetailsModalVisible] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [userProfile, setUserProfile] = useState(null);

  const searchTimeoutRef = useRef(null);
  const flatListRef = useRef(null);

  const tabs = [
    { id: 'all', label: 'All Groups', icon: 'account-group' },
    { id: 'my', label: 'My Groups', icon: 'account' },
    { id: 'popular', label: 'Popular', icon: 'trending-up' },
    { id: 'new', label: 'New', icon: 'new-box' },
    { id: 'engineering', label: 'Engineering', icon: 'engineering' },
  ];

  const subjects = [
    { id: 'all', label: 'All Subjects', icon: 'book' },
    { id: 'software', label: 'Software', icon: 'code-braces' },
    { id: 'mechanical', label: 'Mechanical', icon: 'cog' },
    { id: 'civil', label: 'Civil', icon: 'bridge' },
    { id: 'electrical', label: 'Electrical', icon: 'flash' },
    { id: 'math', label: 'Mathematics', icon: 'calculator' },
    { id: 'physics', label: 'Physics', icon: 'atom' },
    { id: 'chemistry', label: 'Chemistry', icon: 'flask' },
  ];

  const [newGroupForm, setNewGroupForm] = useState({
    name: '',
    subject: '',
    description: '',
    maxMembers: '10',
    privacy: 'public',
    meetingSchedule: '',
    platform: 'zoom',
  });

  const initialStudyGroups = [
    {
      id: 'SG001',
      name: 'Software Engineering Study Group',
      description: 'A group for software engineering students to discuss algorithms, system design, and coding interviews.',
      subject: 'software',
      members: 24,
      maxMembers: 30,
      online: 8,
      createdAt: '2024-01-15',
      nextMeeting: '2024-03-20 18:00',
      meetingFrequency: 'Weekly',
      platform: 'Zoom',
      platformLink: 'https://zoom.us/software-group',
      admin: {
        id: 'A001',
        name: 'Alex Johnson',
        avatar: 'https://randomuser.me/api/portraits/men/32.jpg',
      },
      tags: ['Algorithms', 'System Design', 'Interview Prep'],
      isMember: true,
      isAdmin: false,
      popularity: 95,
    },
    {
      id: 'SG002',
      name: 'Mechanical Engineering Workshop',
      description: 'Discuss mechanical design, thermodynamics, and CAD modeling. Perfect for mechanical engineering majors.',
      subject: 'mechanical',
      members: 18,
      maxMembers: 25,
      online: 5,
      createdAt: '2024-02-01',
      nextMeeting: '2024-03-21 17:30',
      meetingFrequency: 'Bi-weekly',
      platform: 'Google Meet',
      platformLink: 'https://meet.google.com/mechanical-group',
      admin: {
        id: 'A002',
        name: 'Maria Rodriguez',
        avatar: 'https://randomuser.me/api/portraits/women/44.jpg',
      },
      tags: ['CAD', 'Thermodynamics', 'Design'],
      isMember: true,
      isAdmin: false,
      popularity: 88,
    },
    {
      id: 'SG003',
      name: 'Civil Engineering Projects',
      description: 'Group for civil engineering students working on structural analysis and construction projects.',
      subject: 'civil',
      members: 15,
      maxMembers: 20,
      online: 3,
      createdAt: '2024-01-28',
      nextMeeting: '2024-03-22 19:00',
      meetingFrequency: 'Weekly',
      platform: 'Discord',
      platformLink: 'https://discord.gg/civil-engineering',
      admin: {
        id: 'A003',
        name: 'David Chen',
        avatar: 'https://randomuser.me/api/portraits/men/67.jpg',
      },
      tags: ['Structural', 'Construction', 'Materials'],
      isMember: false,
      isAdmin: false,
      popularity: 82,
    },
    {
      id: 'SG004',
      name: 'Electrical Circuits Study',
      description: 'Master electrical circuits, electronics, and power systems with fellow electrical engineering students.',
      subject: 'electrical',
      members: 22,
      maxMembers: 25,
      online: 6,
      createdAt: '2024-02-10',
      nextMeeting: '2024-03-23 16:00',
      meetingFrequency: 'Weekly',
      platform: 'Microsoft Teams',
      platformLink: 'https://teams.microsoft.com/electrical-group',
      admin: {
        id: 'A004',
        name: 'Sarah Williams',
        avatar: 'https://randomuser.me/api/portraits/women/68.jpg',
      },
      tags: ['Circuits', 'Electronics', 'Power Systems'],
      isMember: false,
      isAdmin: false,
      popularity: 90,
    },
    {
      id: 'SG005',
      name: 'Advanced Mathematics Study',
      description: 'Study group for calculus, linear algebra, differential equations, and advanced mathematics topics.',
      subject: 'math',
      members: 30,
      maxMembers: 35,
      online: 12,
      createdAt: '2024-01-05',
      nextMeeting: '2024-03-19 17:00',
      meetingFrequency: 'Twice weekly',
      platform: 'Zoom',
      platformLink: 'https://zoom.us/math-group',
      admin: {
        id: 'A005',
        name: 'James Wilson',
        avatar: 'https://randomuser.me/api/portraits/men/75.jpg',
      },
      tags: ['Calculus', 'Algebra', 'Statistics'],
      isMember: true,
      isAdmin: true,
      popularity: 96,
    },
    {
      id: 'SG006',
      name: 'Physics Problem Solving',
      description: 'Collaborate on physics problems, theory discussions, and lab experiments.',
      subject: 'physics',
      members: 20,
      maxMembers: 25,
      online: 7,
      createdAt: '2024-02-15',
      nextMeeting: '2024-03-24 18:30',
      meetingFrequency: 'Weekly',
      platform: 'Google Meet',
      platformLink: 'https://meet.google.com/physics-group',
      admin: {
        id: 'A006',
        name: 'Lisa Brown',
        avatar: 'https://randomuser.me/api/portraits/women/26.jpg',
      },
      tags: ['Mechanics', 'Thermodynamics', 'Quantum'],
      isMember: false,
      isAdmin: false,
      popularity: 85,
    },
    {
      id: 'SG007',
      name: 'Chemistry Lab Partners',
      description: 'Connect with chemistry students for lab work, experiments, and chemical analysis discussions.',
      subject: 'chemistry',
      members: 16,
      maxMembers: 20,
      online: 4,
      createdAt: '2024-02-20',
      nextMeeting: '2024-03-25 15:00',
      meetingFrequency: 'Weekly',
      platform: 'Discord',
      platformLink: 'https://discord.gg/chemistry-lab',
      admin: {
        id: 'A007',
        name: 'Michael Lee',
        avatar: 'https://randomuser.me/api/portraits/men/55.jpg',
      },
      tags: ['Organic', 'Analytical', 'Physical'],
      isMember: false,
      isAdmin: false,
      popularity: 78,
    },
    {
      id: 'SG008',
      name: 'Data Structures & Algorithms',
      description: 'Focus on DSA problems, coding challenges, and technical interview preparation.',
      subject: 'software',
      members: 28,
      maxMembers: 30,
      online: 10,
      createdAt: '2024-01-20',
      nextMeeting: '2024-03-26 19:30',
      meetingFrequency: 'Daily',
      platform: 'Discord',
      platformLink: 'https://discord.gg/dsa-group',
      admin: {
        id: 'A008',
        name: 'Emma Garcia',
        avatar: 'https://randomuser.me/api/portraits/women/33.jpg',
      },
      tags: ['DSA', 'LeetCode', 'Interview'],
      isMember: true,
      isAdmin: false,
      popularity: 98,
    },
  ];

  useFocusEffect(
    React.useCallback(() => {
      loadInitialData();
    }, [])
  );

  useEffect(() => {
    filterGroups();
  }, [searchQuery, activeTab, allStudyGroups]);

  const loadInitialData = async () => {
    try {
      setLoading(true);
      const [darkModePref, storedGroups, profile] = await Promise.all([
        AsyncStorage.getItem('darkMode'),
        AsyncStorage.getItem('studyGroups'),
        AsyncStorage.getItem('userProfile'),
      ]);
      
      if (darkModePref !== null) {
        setDarkMode(JSON.parse(darkModePref));
      }
      
      if (storedGroups) {
        const groups = JSON.parse(storedGroups);
        setAllStudyGroups(groups);
        
        // Filter my groups
        const userGroups = groups.filter(group => group.isMember);
        setMyGroups(userGroups);
      } else {
        // Use initial data if nothing stored
        setAllStudyGroups(initialStudyGroups);
        
        const userGroups = initialStudyGroups.filter(group => group.isMember);
        setMyGroups(userGroups);
        
        // Save initial data
        await AsyncStorage.setItem('studyGroups', JSON.stringify(initialStudyGroups));
      }
      
      if (profile) {
        setUserProfile(JSON.parse(profile));
      } else {
        // Create default profile
        const defaultProfile = {
          id: 'USER001',
          name: 'Engineering Student',
          email: 'student@example.com',
          avatar: 'https://randomuser.me/api/portraits/men/1.jpg',
          year: 'Junior',
          major: 'Computer Science',
        };
        setUserProfile(defaultProfile);
        await AsyncStorage.setItem('userProfile', JSON.stringify(defaultProfile));
      }
    } catch (error) {
      console.error('Error loading data:', error);
      // Use initial data as fallback
      setAllStudyGroups(initialStudyGroups);
      const userGroups = initialStudyGroups.filter(group => group.isMember);
      setMyGroups(userGroups);
    } finally {
      setLoading(false);
    }
  };

  const onRefresh = async () => {
    setRefreshing(true);
    await loadInitialData();
    setRefreshing(false);
  };

  const filterGroups = () => {
    let filtered = [...allStudyGroups];
    
    // Filter by search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(group =>
        String(group.name ?? '').toLowerCase().includes(query) ||
        String(group.description ?? '').toLowerCase().includes(query) ||
        String(group.subject ?? '').toLowerCase().includes(query) ||
        group.tags.some(tag => tag.toLowerCase().includes(query))
      );
    }
    
    // Filter by active tab
    if (activeTab === 'my') {
      filtered = filtered.filter(group => group.isMember);
    } else if (activeTab === 'popular') {
      filtered = filtered.sort((a, b) => b.popularity - a.popularity);
    } else if (activeTab === 'new') {
      filtered = filtered.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
    } else if (activeTab !== 'all' && activeTab !== 'engineering') {
      filtered = filtered.filter(group => group.subject === activeTab);
    }
    
    setFilteredGroups(filtered);
  };

  const handleSearch = (text) => {
    setSearchQuery(text);
    
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    
    searchTimeoutRef.current = setTimeout(() => {
      filterGroups();
    }, 300);
  };

  const toggleGroupMembership = async (groupId) => {
    try {
      const updatedGroups = allStudyGroups.map(group => {
        if (group.id === groupId) {
          const updatedGroup = {
            ...group,
            isMember: !group.isMember,
            members: group.isMember ? group.members - 1 : group.members + 1,
          };
          
          if (updatedGroup.isMember) {
            Alert.alert('Joined Group', `You have joined ${group.name}`);
          } else {
            Alert.alert('Left Group', `You have left ${group.name}`);
          }
          
          return updatedGroup;
        }
        return group;
      });
      
      setAllStudyGroups(updatedGroups);
      await AsyncStorage.setItem('studyGroups', JSON.stringify(updatedGroups));
      
      // Update my groups
      const userGroups = updatedGroups.filter(group => group.isMember);
      setMyGroups(userGroups);
      
      // Update selected group if open
      if (selectedGroup && selectedGroup.id === groupId) {
        const updatedSelected = updatedGroups.find(g => g.id === groupId);
        setSelectedGroup(updatedSelected);
      }
      
    } catch (error) {
      console.error('Error toggling group membership:', error);
      Alert.alert('Error', 'Failed to update group membership');
    }
  };

  const createStudyGroup = async () => {
    if (!newGroupForm.name.trim() || !newGroupForm.description.trim() || !newGroupForm.subject) {
      Alert.alert('Validation Error', 'Please fill in all required fields');
      return;
    }

    try {
      const newGroup = {
        id: `SG${Date.now()}`,
        name: newGroupForm.name,
        description: newGroupForm.description,
        subject: newGroupForm.subject,
        members: 1,
        maxMembers: parseInt(newGroupForm.maxMembers, 10),
        online: 1,
        createdAt: new Date().toISOString().split('T')[0],
        nextMeeting: 'TBD',
        meetingFrequency: newGroupForm.meetingSchedule || 'Weekly',
        platform: newGroupForm.platform,
        platformLink: `https://${newGroupForm.platform}.com/study-group`,
        admin: {
          id: userProfile?.id || 'USER001',
          name: userProfile?.name || 'You',
          avatar: userProfile?.avatar || 'https://randomuser.me/api/portraits/men/1.jpg',
        },
        tags: [newGroupForm.subject.charAt(0).toUpperCase() + newGroupForm.subject.slice(1)],
        isMember: true,
        isAdmin: true,
        popularity: 50,
      };

      const updatedGroups = [newGroup, ...allStudyGroups];
      setAllStudyGroups(updatedGroups);
      await AsyncStorage.setItem('studyGroups', JSON.stringify(updatedGroups));
      
      // Update my groups
      const userGroups = updatedGroups.filter(group => group.isMember);
      setMyGroups(userGroups);
      
      // Reset form and close modal
      setNewGroupForm({
        name: '',
        subject: '',
        description: '',
        maxMembers: '10',
        privacy: 'public',
        meetingSchedule: '',
        platform: 'zoom',
      });
      setCreateModalVisible(false);
      
      Alert.alert('Success', 'Study group created successfully!');
      
    } catch (error) {
      console.error('Error creating study group:', error);
      Alert.alert('Error', 'Failed to create study group');
    }
  };

  const openMeetingLink = (link) => {
    Linking.openURL(link).catch(err => {
      console.error('Failed to open link:', err);
      Alert.alert('Error', 'Could not open meeting link');
    });
  };

  const shareGroup = async (group) => {
    try {
      await Share.share({
        title: group.name,
        message: `Join our study group: ${group.name}\n\n${group.description}\n\nNext meeting: ${group.nextMeeting}\nPlatform: ${group.platform}\n\nJoin here: ${group.platformLink}`,
        url: group.platformLink,
      });
    } catch (error) {
      console.error('Error sharing group:', error);
    }
  };

  const getSubjectColor = (subject) => {
    const colors = darkMode ? {
      software: '#4c51bf',
      mechanical: '#dd6b20',
      civil: '#2b6cb0',
      electrical: '#d69e2e',
      math: '#805ad5',
      physics: '#319795',
      chemistry: '#d53f8c',
      default: '#718096',
    } : {
      software: '#6366f1',
      mechanical: '#f97316',
      civil: '#3b82f6',
      electrical: '#eab308',
      math: '#8b5cf6',
      physics: '#0d9488',
      chemistry: '#ec4899',
      default: '#6b7280',
    };
    
    return colors[subject] || colors.default;
  };

  const getSubjectIcon = (subject) => {
    const icons = {
      software: 'code-braces',
      mechanical: 'cog',
      civil: 'bridge',
      electrical: 'flash',
      math: 'calculator',
      physics: 'atom',
      chemistry: 'flask',
      default: 'book',
    };
    
    return icons[subject] || icons.default;
  };

  const renderHeader = () => (
    <LinearGradient
      colors={darkMode ? ['#2d3748', '#4a5568'] : ['#667eea', '#764ba2']}
      style={styles.header}
    >
      <TouchableOpacity 
        style={styles.backButton}
        onPress={() => navigation.goBack()}
      >
        <MaterialCommunityIcons name="arrow-left" size={22} color="#ffffff" />
      </TouchableOpacity>
      <View style={styles.headerCenter}>
        <Text style={styles.headerTitle}>👥 Study Groups</Text>
        <Text style={styles.headerSubtitle}>
          Collaborate with fellow engineering students
        </Text>
      </View>
      <TouchableOpacity 
        style={styles.createButton}
        onPress={() => setCreateModalVisible(true)}
      >
        <MaterialCommunityIcons name="plus" size={22} color="#ffffff" />
      </TouchableOpacity>
    </LinearGradient>
  );

  const renderSearchBar = () => (
    <View style={[styles.searchContainer, { backgroundColor: darkMode ? '#4a5568' : '#ffffff' }]}>
      <MaterialCommunityIcons 
        name="magnify" 
        size={18} 
        color={darkMode ? "#a0aec0" : "#6b7280"} 
        style={styles.searchIcon}
      />
      <TextInput
        style={[styles.searchInput, { color: darkMode ? '#ffffff' : '#1f2937' }]}
        placeholder="Search study groups..."
        value={searchQuery}
        onChangeText={handleSearch}
        placeholderTextColor={darkMode ? "#a0aec0" : "#6b7280"}
      />
      {searchQuery.length > 0 && (
        <TouchableOpacity onPress={() => setSearchQuery('')}>
          <MaterialCommunityIcons 
            name="close-circle" 
            size={18} 
            color={darkMode ? "#a0aec0" : "#6b7280"} 
          />
        </TouchableOpacity>
      )}
    </View>
  );

  const renderTabs = () => (
    <FlatList
      horizontal
      data={tabs}
      keyExtractor={(item) => item.id}
      renderItem={({ item }) => (
        <TouchableOpacity
          style={[
            styles.tab,
            activeTab === item.id && styles.activeTab,
            { backgroundColor: darkMode ? (activeTab === item.id ? '#4a5568' : '#2d3748') : '#ffffff' }
          ]}
          onPress={() => setActiveTab(item.id)}
        >
          <MaterialCommunityIcons 
            name={item.icon} 
            size={16} 
            color={activeTab === item.id ? '#667eea' : (darkMode ? '#a0aec0' : '#6b7280')} 
          />
          <Text style={[
            styles.tabText,
            { color: activeTab === item.id ? '#667eea' : (darkMode ? '#a0aec0' : '#6b7280') }
          ]}>
            {item.label}
          </Text>
        </TouchableOpacity>
      )}
      showsHorizontalScrollIndicator={false}
      contentContainerStyle={styles.tabsContent}
    />
  );

  const renderSubjects = () => (
    <View style={styles.subjectsContainer}>
      <Text style={[styles.subjectsTitle, { color: darkMode ? '#ffffff' : '#1f2937' }]}>
        Browse by Subject
      </Text>
      <FlatList
        horizontal
        data={subjects}
        keyExtractor={(item) => item.id}
        renderItem={({ item }) => (
          <TouchableOpacity
            style={[
              styles.subjectChip,
              { 
                backgroundColor: darkMode ? '#2d3748' : '#ffffff',
                borderColor: darkMode ? '#4a5568' : '#e5e7eb'
              }
            ]}
            onPress={() => {
              if (item.id === 'all') {
                setActiveTab('all');
              } else {
                setActiveTab(item.id);
              }
            }}
          >
            <MaterialCommunityIcons 
              name={item.icon} 
              size={16} 
              color={getSubjectColor(item.id)} 
            />
            <Text style={[
              styles.subjectText,
              { color: darkMode ? '#a0aec0' : '#6b7280' }
            ]}>
              {item.label}
            </Text>
          </TouchableOpacity>
        )}
        showsHorizontalScrollIndicator={false}
        contentContainerStyle={styles.subjectsList}
      />
    </View>
  );

  const renderGroupCard = ({ item }) => (
    <TouchableOpacity
      style={[styles.groupCard, { backgroundColor: darkMode ? '#2d3748' : '#ffffff' }]}
      onPress={() => {
        setSelectedGroup(item);
        setDetailsModalVisible(true);
      }}
      activeOpacity={0.8}
    >
      <View style={styles.groupHeader}>
        <View style={styles.groupSubject}>
          <MaterialCommunityIcons 
            name={getSubjectIcon(item.subject)} 
            size={16} 
            color={getSubjectColor(item.subject)} 
          />
          <Text style={[styles.groupSubjectText, { color: getSubjectColor(item.subject) }]}>
            {item.subject.charAt(0).toUpperCase() + item.subject.slice(1)}
          </Text>
        </View>
        
        <View style={styles.groupStatus}>
          {item.isMember && (
            <View style={styles.memberBadge}>
              <MaterialCommunityIcons name="check-circle" size={12} color="#10b981" />
              <Text style={styles.memberText}>Member</Text>
            </View>
          )}
          {item.isAdmin && (
            <View style={styles.adminBadge}>
              <MaterialCommunityIcons name="crown" size={12} color="#f59e0b" />
              <Text style={styles.adminText}>Admin</Text>
            </View>
          )}
        </View>
      </View>
      
      <Text style={[styles.groupName, { color: darkMode ? '#ffffff' : '#1f2937' }]}>
        {item.name}
      </Text>
      
      <Text style={[styles.groupDescription, { color: darkMode ? '#a0aec0' : '#6b7280' }]}>
        {item.description.length > 100 ? item.description.substring(0, 100) + '...' : item.description}
      </Text>
      
      <View style={styles.groupTags}>
        {item.tags.slice(0, 3).map((tag, index) => (
          <View key={index} style={styles.tag}>
            <Text style={styles.tagText}>{tag}</Text>
          </View>
        ))}
      </View>
      
      <View style={styles.groupStats}>
        <View style={styles.statItem}>
          <MaterialCommunityIcons name="account-group" size={14} color={darkMode ? '#a0aec0' : '#6b7280'} />
          <Text style={[styles.statText, { color: darkMode ? '#a0aec0' : '#6b7280' }]}>
            {item.members}/{item.maxMembers}
          </Text>
        </View>
        
        <View style={styles.statItem}>
          <MaterialCommunityIcons name="wifi" size={14} color="#10b981" />
          <Text style={[styles.statText, { color: darkMode ? '#a0aec0' : '#6b7280' }]}>
            {item.online} online
          </Text>
        </View>
        
        <View style={styles.statItem}>
          <MaterialCommunityIcons name="calendar" size={14} color={darkMode ? '#a0aec0' : '#6b7280'} />
          <Text style={[styles.statText, { color: darkMode ? '#a0aec0' : '#6b7280' }]}>
            {item.nextMeeting.split(' ')[0]}
          </Text>
        </View>
      </View>
      
      <View style={styles.groupActions}>
        <TouchableOpacity 
          style={[
            styles.joinButton,
            item.isMember ? styles.leaveButton : styles.joinButtonActive
          ]}
          onPress={(e) => {
            e.stopPropagation();
            toggleGroupMembership(item.id);
          }}
        >
          <MaterialCommunityIcons 
            name={item.isMember ? "account-remove" : "account-plus"} 
            size={16} 
            color="#ffffff" 
          />
          <Text style={styles.joinButtonText}>
            {item.isMember ? 'Leave' : 'Join'}
          </Text>
        </TouchableOpacity>
        
        <TouchableOpacity 
          style={styles.shareButton}
          onPress={(e) => {
            e.stopPropagation();
            shareGroup(item);
          }}
        >
          <MaterialCommunityIcons name="share-variant" size={16} color="#667eea" />
        </TouchableOpacity>
      </View>
    </TouchableOpacity>
  );

  const renderCreateModal = () => (
    <Modal
      visible={createModalVisible}
      animationType="slide"
      transparent={true}
      onRequestClose={() => setCreateModalVisible(false)}
    >
      <View style={styles.modalOverlay}>
        <View style={[styles.createModal, { backgroundColor: darkMode ? '#2d3748' : '#ffffff' }]}>
          <View style={styles.modalHeader}>
            <Text style={[styles.modalTitle, { color: darkMode ? '#ffffff' : '#1f2937' }]}>
              Create Study Group
            </Text>
            <TouchableOpacity 
              onPress={() => setCreateModalVisible(false)}
            >
              <MaterialCommunityIcons name="close" size={24} color={darkMode ? '#a0aec0' : '#6b7280'} />
            </TouchableOpacity>
          </View>
          
          <ScrollView style={styles.modalContent}>
            <View style={styles.formGroup}>
              <Text style={[styles.formLabel, { color: darkMode ? '#a0aec0' : '#6b7280' }]}>
                Group Name *
              </Text>
              <TextInput
                style={[
                  styles.formInput,
                  { 
                    backgroundColor: darkMode ? '#4a5568' : '#f3f4f6',
                    color: darkMode ? '#ffffff' : '#1f2937',
                    borderColor: darkMode ? '#4a5568' : '#d1d5db'
                  }
                ]}
                placeholder="Enter group name"
                value={newGroupForm.name}
                onChangeText={(text) => setNewGroupForm({...newGroupForm, name: text})}
                placeholderTextColor={darkMode ? '#718096' : '#9ca3af'}
              />
            </View>
            
            <View style={styles.formGroup}>
              <Text style={[styles.formLabel, { color: darkMode ? '#a0aec0' : '#6b7280' }]}>
                Subject *
              </Text>
              <ScrollView horizontal showsHorizontalScrollIndicator={false}>
                <View style={styles.subjectOptions}>
                  {subjects.slice(1).map((subject) => (
                    <TouchableOpacity
                      key={subject.id}
                      style={[
                        styles.subjectOption,
                        newGroupForm.subject === subject.id && styles.subjectOptionActive,
                        { backgroundColor: darkMode ? '#4a5568' : '#f3f4f6' }
                      ]}
                      onPress={() => setNewGroupForm({...newGroupForm, subject: subject.id})}
                    >
                      <MaterialCommunityIcons 
                        name={subject.icon} 
                        size={16} 
                        color={newGroupForm.subject === subject.id ? '#667eea' : (darkMode ? '#a0aec0' : '#6b7280')} 
                      />
                      <Text style={[
                        styles.subjectOptionText,
                        newGroupForm.subject === subject.id && styles.subjectOptionTextActive
                      ]}>
                        {subject.label}
                      </Text>
                    </TouchableOpacity>
                  ))}
                </View>
              </ScrollView>
            </View>
            
            <View style={styles.formGroup}>
              <Text style={[styles.formLabel, { color: darkMode ? '#a0aec0' : '#6b7280' }]}>
                Description *
              </Text>
              <TextInput
                style={[
                  styles.formTextarea,
                  { 
                    backgroundColor: darkMode ? '#4a5568' : '#f3f4f6',
                    color: darkMode ? '#ffffff' : '#1f2937',
                    borderColor: darkMode ? '#4a5568' : '#d1d5db'
                  }
                ]}
                placeholder="Describe your study group..."
                value={newGroupForm.description}
                onChangeText={(text) => setNewGroupForm({...newGroupForm, description: text})}
                placeholderTextColor={darkMode ? '#718096' : '#9ca3af'}
                multiline
                numberOfLines={4}
              />
            </View>
            
            <View style={styles.formGroup}>
              <Text style={[styles.formLabel, { color: darkMode ? '#a0aec0' : '#6b7280' }]}>
                Maximum Members
              </Text>
              <View style={styles.membersInput}>
                <TouchableOpacity 
                  style={styles.membersButton}
                  onPress={() => {
                    const current = parseInt(newGroupForm.maxMembers, 10);
                    if (current > 1) {
                      setNewGroupForm({...newGroupForm, maxMembers: (current - 1).toString()});
                    }
                  }}
                >
                  <MaterialCommunityIcons name="minus" size={20} color={darkMode ? '#a0aec0' : '#6b7280'} />
                </TouchableOpacity>
                <Text style={[styles.membersValue, { color: darkMode ? '#ffffff' : '#1f2937' }]}>
                  {newGroupForm.maxMembers}
                </Text>
                <TouchableOpacity 
                  style={styles.membersButton}
                  onPress={() => {
                    const current = parseInt(newGroupForm.maxMembers, 10);
                    if (current < 50) {
                      setNewGroupForm({...newGroupForm, maxMembers: (current + 1).toString()});
                    }
                  }}
                >
                  <MaterialCommunityIcons name="plus" size={20} color={darkMode ? '#a0aec0' : '#6b7280'} />
                </TouchableOpacity>
              </View>
            </View>
            
            <View style={styles.formGroup}>
              <Text style={[styles.formLabel, { color: darkMode ? '#a0aec0' : '#6b7280' }]}>
                Meeting Platform
              </Text>
              <View style={styles.platformOptions}>
                {['zoom', 'google-meet', 'discord', 'teams', 'other'].map((platform) => (
                  <TouchableOpacity
                    key={platform}
                    style={[
                      styles.platformOption,
                      newGroupForm.platform === platform && styles.platformOptionActive,
                      { backgroundColor: darkMode ? '#4a5568' : '#f3f4f6' }
                    ]}
                    onPress={() => setNewGroupForm({...newGroupForm, platform})}
                  >
                    <MaterialCommunityIcons 
                      name={
                        platform === 'zoom' ? 'video' : 
                        platform === 'google-meet' ? 'google' :
                        platform === 'discord' ? 'chat' :
                        platform === 'teams' ? 'microsoft' : 'video'
                      } 
                      size={16} 
                      color={newGroupForm.platform === platform ? '#667eea' : (darkMode ? '#a0aec0' : '#6b7280')} 
                    />
                    <Text style={[
                      styles.platformText,
                      newGroupForm.platform === platform && styles.platformTextActive
                    ]}>
                      {platform.charAt(0).toUpperCase() + platform.slice(1)}
                    </Text>
                  </TouchableOpacity>
                ))}
              </View>
            </View>
            
            <View style={styles.formGroup}>
              <Text style={[styles.formLabel, { color: darkMode ? '#a0aec0' : '#6b7280' }]}>
                Meeting Schedule (Optional)
              </Text>
              <TextInput
                style={[
                  styles.formInput,
                  { 
                    backgroundColor: darkMode ? '#4a5568' : '#f3f4f6',
                    color: darkMode ? '#ffffff' : '#1f2937',
                    borderColor: darkMode ? '#4a5568' : '#d1d5db'
                  }
                ]}
                placeholder="e.g., Weekly on Mondays at 6 PM"
                value={newGroupForm.meetingSchedule}
                onChangeText={(text) => setNewGroupForm({...newGroupForm, meetingSchedule: text})}
                placeholderTextColor={darkMode ? '#718096' : '#9ca3af'}
              />
            </View>
          </ScrollView>
          
          <View style={styles.modalActions}>
            <TouchableOpacity 
              style={styles.cancelModalButton}
              onPress={() => setCreateModalVisible(false)}
            >
              <Text style={styles.cancelModalText}>Cancel</Text>
            </TouchableOpacity>
            <TouchableOpacity 
              style={styles.createModalButton}
              onPress={createStudyGroup}
            >
              <Text style={styles.createModalText}>Create Group</Text>
              <MaterialCommunityIcons name="check" size={18} color="#ffffff" />
            </TouchableOpacity>
          </View>
        </View>
      </View>
    </Modal>
  );

  const renderDetailsModal = () => (
    <Modal
      visible={detailsModalVisible}
      animationType="slide"
      onRequestClose={() => setDetailsModalVisible(false)}
    >
      <SafeAreaView style={[styles.detailModalContainer, { backgroundColor: darkMode ? '#1a202c' : '#ffffff' }]}>
        {selectedGroup && (
          <>
            <LinearGradient
              colors={[getSubjectColor(selectedGroup.subject), `${getSubjectColor(selectedGroup.subject)}80`]}
              style={styles.detailHeader}
            >
              <TouchableOpacity 
                style={styles.detailBackButton}
                onPress={() => setDetailsModalVisible(false)}
              >
                <MaterialCommunityIcons name="close" size={22} color="#ffffff" />
              </TouchableOpacity>
              <View style={styles.detailHeaderCenter}>
                <Text style={styles.detailTitle} numberOfLines={2}>
                  {selectedGroup.name}
                </Text>
                <Text style={styles.detailSubtitle}>
                  {selectedGroup.subject.charAt(0).toUpperCase() + selectedGroup.subject.slice(1)} • {selectedGroup.members} members
                </Text>
              </View>
              <TouchableOpacity 
                style={styles.detailShareButton}
                onPress={() => shareGroup(selectedGroup)}
              >
                <MaterialCommunityIcons name="share-variant" size={22} color="#ffffff" />
              </TouchableOpacity>
            </LinearGradient>

            <ScrollView style={styles.detailContent}>
              <View style={styles.detailOverview}>
                <View style={styles.overviewItem}>
                  <MaterialCommunityIcons name="calendar" size={16} color={darkMode ? "#a0aec0" : "#6b7280"} />
                  <Text style={[styles.overviewLabel, { color: darkMode ? '#a0aec0' : '#6b7280' }]}>
                    Next Meeting
                  </Text>
                  <Text style={[styles.overviewValue, { color: darkMode ? '#ffffff' : '#1f2937' }]}>
                    {selectedGroup.nextMeeting}
                  </Text>
                </View>
                
                <View style={styles.overviewItem}>
                  <MaterialCommunityIcons name="repeat" size={16} color={darkMode ? "#a0aec0" : "#6b7280"} />
                  <Text style={[styles.overviewLabel, { color: darkMode ? '#a0aec0' : '#6b7280' }]}>
                    Frequency
                  </Text>
                  <Text style={[styles.overviewValue, { color: darkMode ? '#ffffff' : '#1f2937' }]}>
                    {selectedGroup.meetingFrequency}
                  </Text>
                </View>
                
                <View style={styles.overviewItem}>
                  <MaterialCommunityIcons name="video" size={16} color={darkMode ? "#a0aec0" : "#6b7280"} />
                  <Text style={[styles.overviewLabel, { color: darkMode ? '#a0aec0' : '#6b7280' }]}>
                    Platform
                  </Text>
                  <Text style={[styles.overviewValue, { color: darkMode ? '#ffffff' : '#1f2937' }]}>
                    {selectedGroup.platform}
                  </Text>
                </View>
                
                <View style={styles.overviewItem}>
                  <MaterialCommunityIcons name="account-group" size={16} color={darkMode ? "#a0aec0" : "#6b7280"} />
                  <Text style={[styles.overviewLabel, { color: darkMode ? '#a0aec0' : '#6b7280' }]}>
                    Capacity
                  </Text>
                  <Text style={[styles.overviewValue, { color: darkMode ? '#ffffff' : '#1f2937' }]}>
                    {selectedGroup.members}/{selectedGroup.maxMembers}
                  </Text>
                </View>
              </View>

              <View style={styles.detailSection}>
                <Text style={[styles.sectionTitle, { color: darkMode ? '#ffffff' : '#1f2937' }]}>
                  Description
                </Text>
                <Text style={[styles.sectionContent, { color: darkMode ? '#cbd5e0' : '#4b5563' }]}>
                  {selectedGroup.description}
                </Text>
              </View>

              <View style={styles.detailSection}>
                <Text style={[styles.sectionTitle, { color: darkMode ? '#ffffff' : '#1f2937' }]}>
                  Group Admin
                </Text>
                <View style={styles.adminInfo}>
                  <View style={styles.adminAvatar}>
                    <MaterialCommunityIcons name="account" size={24} color="#667eea" />
                  </View>
                  <View style={styles.adminDetails}>
                    <Text style={[styles.adminName, { color: darkMode ? '#ffffff' : '#1f2937' }]}>
                      {selectedGroup.admin.name}
                    </Text>
                    <Text style={[styles.adminRole, { color: darkMode ? '#a0aec0' : '#6b7280' }]}>
                      Group Administrator
                    </Text>
                  </View>
                  {selectedGroup.isAdmin && (
                    <View style={styles.youBadge}>
                      <Text style={styles.youText}>You</Text>
                    </View>
                  )}
                </View>
              </View>

              <View style={styles.detailSection}>
                <Text style={[styles.sectionTitle, { color: darkMode ? '#ffffff' : '#1f2937' }]}>
                  Tags & Topics
                </Text>
                <View style={styles.tagsContainer}>
                  {selectedGroup.tags.map((tag, index) => (
                    <View key={index} style={styles.detailTag}>
                      <Text style={styles.detailTagText}>{tag}</Text>
                    </View>
                  ))}
                </View>
              </View>

              <View style={styles.detailSection}>
                <Text style={[styles.sectionTitle, { color: darkMode ? '#ffffff' : '#1f2937' }]}>
                  Group Stats
                </Text>
                <View style={styles.statsContainer}>
                  <View style={styles.statItemDetail}>
                    <Text style={[styles.statNumber, { color: darkMode ? '#ffffff' : '#1f2937' }]}>
                      {selectedGroup.members}
                    </Text>
                    <Text style={[styles.statLabel, { color: darkMode ? '#a0aec0' : '#6b7280' }]}>
                      Total Members
                    </Text>
                  </View>
                  <View style={styles.statItemDetail}>
                    <Text style={[styles.statNumber, { color: darkMode ? '#ffffff' : '#1f2937' }]}>
                      {selectedGroup.online}
                    </Text>
                    <Text style={[styles.statLabel, { color: darkMode ? '#a0aec0' : '#6b7280' }]}>
                      Online Now
                    </Text>
                  </View>
                  <View style={styles.statItemDetail}>
                    <Text style={[styles.statNumber, { color: darkMode ? '#ffffff' : '#1f2937' }]}>
                      {selectedGroup.popularity}%
                    </Text>
                    <Text style={[styles.statLabel, { color: darkMode ? '#a0aec0' : '#6b7280' }]}>
                      Popularity
                    </Text>
                  </View>
                </View>
              </View>
            </ScrollView>

            <View style={[styles.detailFooter, { backgroundColor: darkMode ? '#2d3748' : '#ffffff' }]}>
              <TouchableOpacity 
                style={styles.joinMeetingButton}
                onPress={() => openMeetingLink(selectedGroup.platformLink)}
              >
                <MaterialCommunityIcons name="video" size={20} color="#ffffff" />
                <Text style={styles.joinMeetingText}>Join Meeting</Text>
              </TouchableOpacity>
              
              <TouchableOpacity 
                style={[
                  styles.membershipButton,
                  selectedGroup.isMember ? styles.leaveButtonDetail : styles.joinButtonDetail
                ]}
                onPress={() => {
                  toggleGroupMembership(selectedGroup.id);
                }}
              >
                <MaterialCommunityIcons 
                  name={selectedGroup.isMember ? "account-remove" : "account-plus"} 
                  size={20} 
                  color="#ffffff" 
                />
                <Text style={styles.membershipButtonText}>
                  {selectedGroup.isMember ? 'Leave Group' : 'Join Group'}
                </Text>
              </TouchableOpacity>
            </View>
          </>
        )}
      </SafeAreaView>
    </Modal>
  );

  const renderEmptyState = () => (
    <View style={styles.emptyState}>
      <MaterialCommunityIcons 
        name="account-group" 
        size={64} 
        color={darkMode ? "#4a5568" : "#d1d5db"} 
      />
      <Text style={[styles.emptyStateTitle, { color: darkMode ? '#f7fafc' : '#1f2937' }]}>
        No Study Groups Found
      </Text>
      <Text style={[styles.emptyStateText, { color: darkMode ? '#a0aec0' : '#6b7280' }]}>
        {activeTab === 'my' 
          ? "You haven't joined any study groups yet. Join one to get started!"
          : "No groups match your search. Try adjusting your filters or create a new group!"}
      </Text>
      {activeTab === 'my' ? (
        <TouchableOpacity 
          style={styles.browseButton}
          onPress={() => setActiveTab('all')}
        >
          <Text style={styles.browseButtonText}>Browse Groups</Text>
        </TouchableOpacity>
      ) : (
        <TouchableOpacity 
          style={styles.createEmptyButton}
          onPress={() => setCreateModalVisible(true)}
        >
          <Text style={styles.createEmptyButtonText}>Create First Group</Text>
        </TouchableOpacity>
      )}
    </View>
  );

  if (loading) {
    return (
      <SafeAreaView style={[styles.loadingContainer, { backgroundColor: darkMode ? '#1a202c' : '#f0f4f8' }]}>
        <ActivityIndicator size="large" color="#667eea" />
        <Text style={[styles.loadingText, { color: darkMode ? '#cbd5e0' : '#6b7280' }]}>
          Loading study groups...
        </Text>
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={[styles.safeArea, { backgroundColor: darkMode ? '#1a202c' : '#f0f4f8' }]}>
      <StatusBar barStyle={darkMode ? "light-content" : "light-content"} backgroundColor="#667eea" />
      
      {renderHeader()}
      {renderSearchBar()}
      
      <FlatList
        ref={flatListRef}
        data={filteredGroups}
        keyExtractor={(item) => item.id}
        renderItem={renderGroupCard}
        contentContainerStyle={styles.scrollContent}
        showsVerticalScrollIndicator={false}
        refreshControl={
          <RefreshControl
            refreshing={refreshing}
            onRefresh={onRefresh}
            colors={['#667eea']}
            tintColor="#667eea"
          />
        }
        ListHeaderComponent={
          <>
            {renderTabs()}
            {renderSubjects()}
            
            <View style={styles.statsOverview}>
              <View style={[styles.statCard, { backgroundColor: darkMode ? '#2d3748' : '#ffffff' }]}>
                <MaterialCommunityIcons name="account-group" size={20} color="#667eea" />
                <Text style={[styles.statNumber, { color: darkMode ? '#ffffff' : '#1f2937' }]}>
                  {myGroups.length}
                </Text>
                <Text style={[styles.statLabel, { color: darkMode ? '#a0aec0' : '#6b7280' }]}>
                  My Groups
                </Text>
              </View>
              
              <View style={[styles.statCard, { backgroundColor: darkMode ? '#2d3748' : '#ffffff' }]}>
                <MaterialCommunityIcons name="wifi" size={20} color="#10b981" />
                <Text style={[styles.statNumber, { color: darkMode ? '#ffffff' : '#1f2937' }]}>
                  {allStudyGroups.reduce((sum, group) => sum + group.online, 0)}
                </Text>
                <Text style={[styles.statLabel, { color: darkMode ? '#a0aec0' : '#6b7280' }]}>
                  Online Now
                </Text>
              </View>
              
              <View style={[styles.statCard, { backgroundColor: darkMode ? '#2d3748' : '#ffffff' }]}>
                <MaterialCommunityIcons name="trending-up" size={20} color="#f59e0b" />
                <Text style={[styles.statNumber, { color: darkMode ? '#ffffff' : '#1f2937' }]}>
                  {allStudyGroups.length}
                </Text>
                <Text style={[styles.statLabel, { color: darkMode ? '#a0aec0' : '#6b7280' }]}>
                  Total Groups
                </Text>
              </View>
              
              <View style={[styles.statCard, { backgroundColor: darkMode ? '#2d3748' : '#ffffff' }]}>
                <MaterialCommunityIcons name="crown" size={20} color="#8b5cf6" />
                <Text style={[styles.statNumber, { color: darkMode ? '#ffffff' : '#1f2937' }]}>
                  {allStudyGroups.filter(g => g.isAdmin).length}
                </Text>
                <Text style={[styles.statLabel, { color: darkMode ? '#a0aec0' : '#6b7280' }]}>
                  Admin Of
                </Text>
              </View>
            </View>
            
            <View style={styles.sectionHeader}>
              <Text style={[styles.sectionTitle, { color: darkMode ? '#ffffff' : '#1f2937' }]}>
                {activeTab === 'my' ? 'My Study Groups' : 
                 activeTab === 'popular' ? 'Popular Groups' :
                 activeTab === 'new' ? 'New Groups' :
                 activeTab === 'engineering' ? 'Engineering Groups' : 'All Study Groups'}
              </Text>
              <Text style={[styles.sectionSubtitle, { color: darkMode ? '#a0aec0' : '#6b7280' }]}>
                {filteredGroups.length} groups found
              </Text>
            </View>
          </>
        }
        ListEmptyComponent={renderEmptyState}
      />

      {renderCreateModal()}
      {renderDetailsModal()}
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  safeArea: {
    flex: 1,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  loadingText: {
    marginTop: 12,
    fontSize: 14,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
    paddingVertical: 12,
  },
  backButton: {
    padding: 6,
  },
  headerCenter: {
    flex: 1,
    alignItems: 'center',
  },
  headerTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#ffffff',
  },
  headerSubtitle: {
    fontSize: 11,
    color: 'rgba(255, 255, 255, 0.8)',
    marginTop: 2,
  },
  createButton: {
    padding: 6,
  },
  searchContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    marginHorizontal: 16,
    marginVertical: 12,
    borderRadius: 10,
    paddingHorizontal: 12,
    height: 40,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.08,
    shadowRadius: 3,
    elevation: 2,
  },
  searchIcon: {
    marginRight: 8,
  },
  searchInput: {
    flex: 1,
    height: 40,
    fontSize: 14,
  },
  scrollContent: {
    paddingBottom: 16,
  },
  tabsContent: {
    paddingHorizontal: 16,
    paddingVertical: 8,
  },
  tab: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 20,
    marginRight: 8,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.08,
    shadowRadius: 2,
    elevation: 1,
  },
  activeTab: {
    borderWidth: 1,
    borderColor: '#667eea',
  },
  tabText: {
    fontSize: 13,
    fontWeight: '500',
    marginLeft: 6,
  },
  subjectsContainer: {
    paddingHorizontal: 16,
    marginBottom: 16,
  },
  subjectsTitle: {
    fontSize: 16,
    fontWeight: 'bold',
    marginBottom: 8,
  },
  subjectsList: {
    paddingRight: 16,
  },
  subjectChip: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 16,
    borderWidth: 1,
    marginRight: 8,
  },
  subjectText: {
    fontSize: 12,
    marginLeft: 4,
  },
  statsOverview: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    paddingHorizontal: 16,
    marginBottom: 16,
    gap: 8,
  },
  statCard: {
    flex: 1,
    minWidth: '45%',
    alignItems: 'center',
    padding: 12,
    borderRadius: 10,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.08,
    shadowRadius: 3,
    elevation: 2,
  },
  statNumber: {
    fontSize: 18,
    fontWeight: 'bold',
    marginVertical: 4,
  },
  statLabel: {
    fontSize: 10,
    textAlign: 'center',
  },
  sectionHeader: {
    paddingHorizontal: 16,
    marginBottom: 12,
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: 'bold',
  },
  sectionSubtitle: {
    fontSize: 13,
    marginTop: 2,
  },
  groupCard: {
    marginHorizontal: 16,
    marginBottom: 12,
    padding: 16,
    borderRadius: 12,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.08,
    shadowRadius: 3,
    elevation: 2,
  },
  groupHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 12,
  },
  groupSubject: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  groupSubjectText: {
    fontSize: 12,
    fontWeight: '600',
    marginLeft: 4,
  },
  groupStatus: {
    flexDirection: 'row',
    gap: 6,
  },
  memberBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: 'rgba(16, 185, 129, 0.1)',
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 4,
  },
  memberText: {
    fontSize: 10,
    color: '#10b981',
    marginLeft: 2,
    fontWeight: '500',
  },
  adminBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: 'rgba(245, 158, 11, 0.1)',
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 4,
  },
  adminText: {
    fontSize: 10,
    color: '#f59e0b',
    marginLeft: 2,
    fontWeight: '500',
  },
  groupName: {
    fontSize: 16,
    fontWeight: 'bold',
    marginBottom: 8,
    lineHeight: 20,
  },
  groupDescription: {
    fontSize: 13,
    marginBottom: 12,
    lineHeight: 18,
  },
  groupTags: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 6,
    marginBottom: 12,
  },
  tag: {
    backgroundColor: '#e5e7eb',
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 4,
  },
  tagText: {
    fontSize: 10,
    color: '#6b7280',
    fontWeight: '500',
  },
  groupStats: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 12,
  },
  statItem: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  statText: {
    fontSize: 12,
    marginLeft: 4,
  },
  groupActions: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  joinButton: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 6,
    flex: 1,
    justifyContent: 'center',
    marginRight: 8,
  },
  joinButtonActive: {
    backgroundColor: '#667eea',
  },
  leaveButton: {
    backgroundColor: '#ef4444',
  },
  joinButtonText: {
    color: '#ffffff',
    fontSize: 13,
    fontWeight: '600',
    marginLeft: 4,
  },
  shareButton: {
    padding: 8,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: '#667eea',
  },
  emptyState: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 40,
    paddingHorizontal: 16,
  },
  emptyStateTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    marginTop: 16,
  },
  emptyStateText: {
    fontSize: 14,
    marginTop: 8,
    textAlign: 'center',
    lineHeight: 20,
  },
  browseButton: {
    backgroundColor: '#667eea',
    paddingHorizontal: 24,
    paddingVertical: 12,
    borderRadius: 8,
    marginTop: 24,
  },
  browseButtonText: {
    color: '#ffffff',
    fontSize: 14,
    fontWeight: '600',
  },
  createEmptyButton: {
    backgroundColor: '#10b981',
    paddingHorizontal: 24,
    paddingVertical: 12,
    borderRadius: 8,
    marginTop: 24,
  },
  createEmptyButtonText: {
    color: '#ffffff',
    fontSize: 14,
    fontWeight: '600',
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'flex-end',
  },
  createModal: {
    borderTopLeftRadius: 16,
    borderTopRightRadius: 16,
    maxHeight: '90%',
  },
  modalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#e5e7eb',
  },
  modalTitle: {
    fontSize: 18,
    fontWeight: 'bold',
  },
  modalContent: {
    padding: 16,
    maxHeight: '80%',
  },
  formGroup: {
    marginBottom: 16,
  },
  formLabel: {
    fontSize: 13,
    fontWeight: '600',
    marginBottom: 6,
  },
  formInput: {
    borderWidth: 1,
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 10,
    fontSize: 14,
  },
  formTextarea: {
    borderWidth: 1,
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 10,
    fontSize: 14,
    minHeight: 100,
    textAlignVertical: 'top',
  },
  subjectOptions: {
    flexDirection: 'row',
    gap: 8,
    paddingVertical: 2,
  },
  subjectOption: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 8,
  },
  subjectOptionActive: {
    borderWidth: 1,
    borderColor: '#667eea',
  },
  subjectOptionText: {
    fontSize: 12,
    marginLeft: 4,
  },
  subjectOptionTextActive: {
    color: '#667eea',
    fontWeight: '600',
  },
  membersInput: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#f3f4f6',
    borderRadius: 8,
    padding: 8,
  },
  membersButton: {
    padding: 8,
  },
  membersValue: {
    fontSize: 16,
    fontWeight: 'bold',
    marginHorizontal: 16,
  },
  platformOptions: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
  platformOption: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 8,
  },
  platformOptionActive: {
    borderWidth: 1,
    borderColor: '#667eea',
  },
  platformText: {
    fontSize: 12,
    marginLeft: 4,
  },
  platformTextActive: {
    color: '#667eea',
    fontWeight: '600',
  },
  modalActions: {
    flexDirection: 'row',
    padding: 16,
    borderTopWidth: 1,
    borderTopColor: '#e5e7eb',
    gap: 12,
  },
  cancelModalButton: {
    flex: 1,
    backgroundColor: '#e5e7eb',
    paddingVertical: 12,
    borderRadius: 8,
    alignItems: 'center',
  },
  cancelModalText: {
    fontSize: 14,
    fontWeight: '600',
    color: '#6b7280',
  },
  createModalButton: {
    flex: 2,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#667eea',
    paddingVertical: 12,
    borderRadius: 8,
    gap: 8,
  },
  createModalText: {
    fontSize: 14,
    fontWeight: '600',
    color: '#ffffff',
  },
  detailModalContainer: {
    flex: 1,
  },
  detailHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 12,
  },
  detailBackButton: {
    padding: 6,
  },
  detailHeaderCenter: {
    flex: 1,
    marginHorizontal: 10,
  },
  detailTitle: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#ffffff',
  },
  detailSubtitle: {
    fontSize: 12,
    color: 'rgba(255, 255, 255, 0.8)',
    marginTop: 4,
  },
  detailShareButton: {
    padding: 6,
  },
  detailContent: {
    flex: 1,
    padding: 16,
  },
  detailOverview: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    backgroundColor: '#f3f4f6',
    padding: 12,
    borderRadius: 10,
    marginBottom: 16,
    gap: 12,
  },
  overviewItem: {
    width: '48%',
  },
  overviewLabel: {
    fontSize: 11,
    marginTop: 4,
  },
  overviewValue: {
    fontSize: 12,
    fontWeight: '600',
    marginTop: 2,
  },
  detailSection: {
    marginBottom: 20,
  },
  sectionTitle: {
    fontSize: 16,
    fontWeight: 'bold',
    marginBottom: 10,
  },
  sectionContent: {
    fontSize: 14,
    lineHeight: 20,
  },
  adminInfo: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#f3f4f6',
    padding: 12,
    borderRadius: 8,
  },
  adminAvatar: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: '#e5e7eb',
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 12,
  },
  adminDetails: {
    flex: 1,
  },
  adminName: {
    fontSize: 14,
    fontWeight: 'bold',
  },
  adminRole: {
    fontSize: 12,
    marginTop: 2,
  },
  youBadge: {
    backgroundColor: '#667eea',
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 4,
  },
  youText: {
    fontSize: 10,
    color: '#ffffff',
    fontWeight: '500',
  },
  tagsContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
  detailTag: {
    backgroundColor: '#e5e7eb',
    paddingHorizontal: 10,
    paddingVertical: 6,
    borderRadius: 6,
  },
  detailTagText: {
    fontSize: 12,
    color: '#6b7280',
    fontWeight: '500',
  },
  statsContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    backgroundColor: '#f3f4f6',
    padding: 12,
    borderRadius: 10,
  },
  statItemDetail: {
    alignItems: 'center',
    flex: 1,
  },
  statNumber: {
    fontSize: 16,
    fontWeight: 'bold',
  },
  statLabel: {
    fontSize: 10,
    marginTop: 4,
  },
  detailFooter: {
    flexDirection: 'row',
    padding: 16,
    borderTopWidth: 1,
    borderTopColor: '#e5e7eb',
    gap: 12,
  },
  joinMeetingButton: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#10b981',
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderRadius: 8,
    flex: 1,
    justifyContent: 'center',
    gap: 8,
  },
  joinMeetingText: {
    color: '#ffffff',
    fontSize: 14,
    fontWeight: '600',
  },
  membershipButton: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderRadius: 8,
    flex: 1,
    justifyContent: 'center',
    gap: 8,
  },
  joinButtonDetail: {
    backgroundColor: '#667eea',
  },
  leaveButtonDetail: {
    backgroundColor: '#ef4444',
  },
  membershipButtonText: {
    color: '#ffffff',
    fontSize: 14,
    fontWeight: '600',
  },
});

export default StudyGroups;